import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Etiqueta, Painel, type TomEtiqueta } from '../comum';

/**
 * Operation Center: o estado da operação numa tela.
 *
 * 🔴 **`desconhecido` é um estado de primeira classe.** Sem briefing de hoje, a
 * faixa não fica verde nem cinza discreta — ela diz que o produto não sabe como
 * o parque está. Um painel de comando que mostra "ok" por falta de dado é o
 * modo de falha mais caro que ele tem: a pessoa vai embora tranquila.
 *
 * 🔴 **E toda linha traz a saída junto.** Problema aparece com a recomendação
 * ao lado. Alerta sem ação é alarme, e alarme é o que as pessoas desligam.
 */

interface Recomendacao {
  readonly problemId: string;
  readonly acao: string;
  readonly titulo: string;
  readonly justificativa: string;
  readonly automatizavel: boolean;
  readonly capacidade: string | null;
}

interface ProblemaDoCentro {
  readonly id: string;
  readonly title: string;
  readonly severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  readonly affectedDevices: { readonly count: number };
}

interface Centro {
  readonly saude: {
    readonly nivel: 'ok' | 'atencao' | 'critico' | 'desconhecido';
    readonly motivo: string;
  };
  readonly briefing: {
    readonly dia: string;
    readonly doDia: boolean;
    readonly resumo: string;
    readonly incertezas: readonly string[];
  } | null;
  readonly problemas: readonly ProblemaDoCentro[];
  readonly recomendacoes: readonly Recomendacao[];
  readonly tendencias: {
    readonly periodo: string;
    readonly aviso: string | null;
    readonly piores: readonly { readonly rotulo: string; readonly delta: number | null }[];
  } | null;
  readonly anomalias: {
    readonly itens: readonly { readonly rotulo: string; readonly valor: number; readonly mediana: number }[];
    readonly aviso: string | null;
  } | null;
  readonly operacao: {
    readonly aprovacoesPendentes: number;
    readonly aprovadasNaoExecutadas: number;
    readonly fila: Readonly<Record<string, number>> | null;
  };
  readonly recorrentes: readonly { readonly id: string; readonly titulo: string; readonly ocorrencias: number }[];
  readonly indisponiveis: readonly string[];
}

const SAUDE: Readonly<Record<Centro['saude']['nivel'], { rotulo: string; tom: TomEtiqueta }>> = {
  ok: { rotulo: 'Sob controle', tom: 'ok' },
  atencao: { rotulo: 'Atenção', tom: 'alerta' },
  critico: { rotulo: 'Crítico', tom: 'erro' },
  // Não é neutro por acaso — é o tom de "não sabemos", que é diferente de "ok".
  desconhecido: { rotulo: 'Sem retrato de hoje', tom: 'alerta' },
};

/**
 * A severidade chega em inglês, do KACE. Na tela ela sai em português.
 *
 * ⚠️ O tom não é decoração: `HIGH` e `CRITICAL` dividem o mesmo vermelho de
 * propósito — a diferença entre os dois não muda o que a pessoa faz hoje.
 * `LOW` fica neutro para não competir com o que exige ação.
 */
const SEVERIDADE: Readonly<Record<string, { rotulo: string; tom: TomEtiqueta }>> = {
  CRITICAL: { rotulo: 'Crítico', tom: 'erro' },
  HIGH: { rotulo: 'Alto', tom: 'erro' },
  MEDIUM: { rotulo: 'Médio', tom: 'alerta' },
  LOW: { rotulo: 'Baixo', tom: 'neutro' },
};

export function Centro({
  aoPerderSessao,
  irPara,
}: {
  aoPerderSessao: () => void;
  irPara: (aba: 'aprovacoes' | 'execucoes' | 'problemas') => void;
}): ReactNode {
  const [dados, setDados] = useState<Centro | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setDados(await api.get<Centro>('/api/centro'));
    } catch (e: unknown) {
      if ((e as { precisaLogin?: boolean }).precisaLogin === true) aoPerderSessao();
    }
  }, [aoPerderSessao]);

  useEffect(() => {
    void carregar();
    const t = setInterval(() => void carregar(), 60_000);
    return () => clearInterval(t);
  }, [carregar]);

  if (dados === null) return null;

  const saude = SAUDE[dados.saude.nivel];
  const porProblema = new Map(dados.recomendacoes.map((r) => [r.problemId, r]));

  return (
    <Painel titulo="Centro de operação" sub="Saúde, o que mudou e o que espera decisão.">
      <div className="linha" style={{ marginBottom: 12 }}>
        <strong>{dados.saude.motivo}</strong>
        <Etiqueta tom={saude.tom}>{saude.rotulo}</Etiqueta>
      </div>

      {/*
        🔴 O que não deu para montar aparece. Um campo vazio por falha e um
        campo vazio por não haver nada são a mesma tela sem este aviso — e a
        primeira leitura é sempre a tranquilizadora.
      */}
      {dados.indisponiveis.length > 0 && (
        <Aviso tipo="alerta">
          Não foi possível montar: {dados.indisponiveis.join(', ')}. Os números
          abaixo estão incompletos.
        </Aviso>
      )}

      {dados.anomalias !== null && dados.anomalias.itens.length > 0 && (
        <>
          <h4>Fora do padrão hoje</h4>
          {dados.anomalias.itens.map((a) => (
            <p key={a.rotulo} className="suave">
              <strong>{a.rotulo}</strong>: {a.valor} hoje, contra {a.mediana} de mediana.
            </p>
          ))}
        </>
      )}

      {dados.problemas.length > 0 && (
        <>
          <h4>O que exige ação</h4>
          {dados.problemas.map((p) => {
            const rec = porProblema.get(p.id);
            return (
              <div key={p.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--borda)' }}>
                <div className="linha">
                  <span>
                    <strong>{p.title}</strong>{' '}
                    <span className="suave">· {p.affectedDevices.count} máquina(s)</span>
                  </span>
                  <Etiqueta tom={SEVERIDADE[p.severity]?.tom ?? 'neutro'}>
                    {/* Severidade desconhecida aparece crua: inventar rótulo
                        esconderia um valor novo vindo do appliance. */}
                    {SEVERIDADE[p.severity]?.rotulo ?? p.severity}
                  </Etiqueta>
                </div>
                {rec !== undefined && (
                  <p className="suave">
                    → {rec.titulo}
                    {/*
                      "O produto faz sozinho" é uma promessa. Só aparece para o
                      que ele comprovadamente sabe executar.
                    */}
                    {rec.automatizavel && (
                      <>
                        {' '}
                        <Etiqueta tom="ok">o produto pode executar</Etiqueta>
                      </>
                    )}
                  </p>
                )}
              </div>
            );
          })}
        </>
      )}

      {dados.tendencias !== null && dados.tendencias.piores.length > 0 && (
        <>
          <h4>Piorou nos {dados.tendencias.periodo}</h4>
          {dados.tendencias.piores.map((t) => (
            <p key={t.rotulo} className="suave">
              {t.rotulo}: {(t.delta ?? 0) > 0 ? '+' : ''}
              {t.delta}
            </p>
          ))}
        </>
      )}

      {dados.recorrentes.length > 0 && (
        <>
          <h4>Já aconteceu antes</h4>
          {dados.recorrentes.map((m) => (
            <p key={m.id} className="suave">
              {m.titulo} — {m.ocorrencias}× registradas
            </p>
          ))}
        </>
      )}

      <h4>Esperando decisão</h4>
      <div className="linha">
        <button className="encolhe" onClick={() => irPara('aprovacoes')}>
          {dados.operacao.aprovacoesPendentes} aprovação(ões) pendente(s)
        </button>
        {/*
          🔴 Aprovado e não executado é a fila que ninguém olha: alguém decidiu
          "pode" e a ação nunca saiu. Sem este botão, ela some da vista.
        */}
        {dados.operacao.aprovadasNaoExecutadas > 0 && (
          <button className="encolhe primario" onClick={() => irPara('aprovacoes')}>
            {dados.operacao.aprovadasNaoExecutadas} aprovada(s) sem executar
          </button>
        )}
        {dados.operacao.fila !== null && (
          <button className="encolhe" onClick={() => irPara('execucoes')}>
            fila: {dados.operacao.fila['PENDING'] ?? 0} na espera ·{' '}
            {dados.operacao.fila['FAILED'] ?? 0} com falha
          </button>
        )}
      </div>

      {dados.briefing !== null && dados.briefing.incertezas.length > 0 && (
        <Aviso tipo="alerta">
          <strong>O que não foi possível medir:</strong>
          <ul>
            {dados.briefing.incertezas.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </Aviso>
      )}
    </Painel>
  );
}
